import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogOut, Shield } from 'lucide-react';
import logo from '../../assets/logo.png';
import { signOut } from '../../utils/auth';
import Seo from '../common/Seo';

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = useMemo(() => [
    { label: 'Admin Panel', icon: Shield, path: '/dashboard/admin' }
  ], []);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const allowedNavItems = navItems;

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="min-h-screen bg-[#021206] text-white flex">
      <Seo
        title="Admin Dashboard"
        description="GameMeetsWorld admin management panel."
        path="/dashboard/admin"
        noIndex
      />
      {/* Sidebar */}
      <motion.aside
        initial={{ x: '-100%' }}
        animate={{ x: 0 }}
        transition={{ duration: 0.3 }}
        className={`fixed lg:relative lg:w-64 w-64 h-full bg-[#0A0E11] border-r border-[#A1FF4F]/10 z-40 lg:z-10 flex flex-col ${
          !sidebarOpen ? 'hidden lg:flex' : ''
        }`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-[#A1FF4F]/10">
          <Link to="/" className="flex items-center gap-2 hover:text-[#A1FF4F] transition-colors">
            <img src={logo} alt="Logo" className="h-8 w-auto" />
            <span className="font-bold hidden sm:block">GameMeetsWorld</span>
          </Link>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-6 space-y-2">
          {allowedNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-all ${
                isActive(item.path)
                  ? 'bg-[#A1FF4F]/20 border border-[#A1FF4F]/50 text-[#A1FF4F]'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="p-6 border-t border-[#A1FF4F]/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-[#A1FF4F]/20 hover:bg-[#A1FF4F]/30 text-[#A1FF4F] font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-[#0A0E11]/80 backdrop-blur-xl border-b border-[#A1FF4F]/10 p-6 flex items-center justify-between sticky top-0 z-30">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {sidebarOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </header>

        {/* Overlay for mobile */}
        <AnimatePresence>
          {sidebarOpen && isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            />
          )}
        </AnimatePresence>

        {/* Page Content */}
        <main className="flex-1 p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
